import { type FC, type FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import type { SingleValue } from 'react-select';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { errorToast, toNumericId } from '@/helpers';
import { _notNil } from '@/littledash';
import type { Animal, AnimalSex } from '@/model/Animal.model';
import type { StudyApiId } from '@/model/Study.model';
import { getSexOptions } from '@/model/Animal.utils';
import { featuresSelector } from '@/support/Selectors';
import { modalAction } from '@/utils/modal';
import Select, { type SelectOption } from '@/components/UI/Select/Select';
import { useApiHook } from '@/support/Hooks/api/useApiHook';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

const reasonForChangeSelector = createSelector([featuresSelector], (features) => ({
  reasonForChangeEnabled: features?.reason_for_change === true,
}));

interface UpdateSexProps {
  subjects: Array<Animal>;
  studyApiId: StudyApiId;
  handleCallback: (message: string) => void;
  closeModal: () => void;
}

const UpdateSex: FC<UpdateSexProps> = ({ subjects, studyApiId, handleCallback, closeModal }) => {
  const [sex, setSex] = useState<AnimalSex>('m');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const { reasonForChangeEnabled } = useSelector(reasonForChangeSelector);
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const sexOptions = getSexOptions(unknownSexEnabled);

  const {
    invoke: updateSex,
    loading: isLoading,
    response: updateResponse,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/animals/sex',
    options: { invokeOnInit: false, onError: { throw: false, capture: true, toast: false, slug: 'animal-sex-update' } },
  });

  const what = subjects.length > 1 ? 'animals' : 'animal';
  const whatDemonstrative = what === 'animals' ? 'these' : 'this';

  const saveSex = async (reasonForChange?: string) => {
    const animalIds = subjects.map((s) => toNumericId(s.id));
    const result = await updateSex({
      path: { studyApiId },
      body: {
        animal_ids: animalIds,
        sex,
        ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
      },
    });
    if (result.type === 'success') {
      handleCallback('Successfully updated sex');
    } else if (reasonForChangeEnabled) {
      // The reason-for-change modal has replaced this modal, so the inline ApiErrorBanner
      // is unmounted; surface the failure via a toast that survives the unmount.
      errorToast('There was a problem updating the sex');
    }
  };

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    if (!unknownSexEnabled && sex === 'u') {
      setValidationError('Unknown sex is not available');
      return;
    }
    if (reasonForChangeEnabled) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Animal information has changed',
        onClick: (reasonForChange?: string) => saveSex(reasonForChange),
      });
      return;
    }
    await saveSex();
  };

  const defaultOption = sexOptions.find(({ value }) => value === sex);

  return (
    <form onSubmit={submitForm} className="ui__narrow-modal" data-testid="update-sex-form">
      <div className="mb3 tc">
        <h3 className="normal f4 pb2" data-testid="update-sex-heading">
          Update sex of {whatDemonstrative} {what}?
        </h3>
      </div>
      {updateResponse?.type === 'error' && <ApiErrorBanner error={updateResponse.error} />}
      {validationError && (
        <p className="red" data-testid="update-sex-error">
          {validationError}
        </p>
      )}
      <div className="mb3" data-testid="update-sex-select">
        <Select
          defaultValue={defaultOption}
          options={sexOptions}
          isMulti={false}
          onChange={(option: SingleValue<SelectOption<string>>) => {
            if (option) {
              setSex(option.value as AnimalSex);
            }
          }}
          name="sex"
        />
      </div>
      <div>
        <button type="submit" disabled={isLoading} className="button w-100" data-testid="update-sex-submit">
          Update sex
        </button>
        <button
          type="button"
          className="button plain db w-100 mt2"
          onClick={closeModal}
          data-testid="update-sex-cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default UpdateSex;
